<template>
  <div class="week-costs-modal" v-if="visible">
    <div class="modal-overlay" @click="closeModal"></div>
    <div class="modal-content">
      <div class="modal-header">
        <h2>
          <span class="neon-text">DESPESAS SEMANAIS</span>
          <span class="date">{{ currentDate }}</span>
        </h2>
        <div class="corporate-id">DEEP RING CORP || {{ player.companyName }}</div>
      </div>
      
      <div class="modal-body">
        <div class="scanner-line"></div>
        
        <template v-if="processingState === 'processing'">
          <div class="loading-container">
            <div class="loading-text">
              <span>PROCESSANDO</span>
              <span class="dots">...</span>
            </div>
            <div class="progress-bar">
              <div class="progress-fill"></div>
            </div>
          </div>
        </template>
        
        <template v-else-if="processingState === 'completed'">
          <!-- Saldo atual antes das deduções -->
          <div class="current-balance">
            <div class="balance-title">SALDO ATUAL</div>
            <div class="balance-amount">
              <span class="currency"><i class="coin-icon"></i></span>
              <animated-number :value="initialBalance" :duration="1500" />
            </div>
          </div>

          <!-- Separador -->
          <div class="separator">
            <span class="separator-line"></span>
            <span class="separator-text">DEDUÇÕES</span>
            <span class="separator-line"></span>
          </div>
          
          <div class="section" v-if="financialDetails.fighters.length > 0">
            <h3>SALÁRIOS DOS LUTADORES</h3>
            <div class="expense-list">
              <div class="expense-item" v-for="(fighter, index) in financialDetails.fighters" :key="fighter.id">
                <div class="expense-info">
                  <span class="expense-name">{{ fighter.name }}</span>
                  <span class="expense-value">
                    <span class="currency"><i class="coin-icon"></i></span>
                    <animated-number :value="fighter.amount" />
                  </span>
                </div>
                <div class="transaction-line" :style="{ animationDelay: `${index * 0.2}s` }"></div>
              </div>
            </div>
          </div>
          
          <div class="section" v-if="financialDetails.businesses.length > 0">
            <h3>IMPOSTOS DAS EMPRESAS</h3>
            <div class="expense-list">
              <div class="expense-item" v-for="(business, index) in financialDetails.businesses" :key="business.id">
                <div class="expense-info">
                  <span class="expense-name">{{ business.name }}</span>
                  <span class="expense-value">
                    <span class="currency"><i class="coin-icon"></i></span>
                    <animated-number :value="business.amount" />
                  </span>
                </div>
                <div class="transaction-line" :style="{ animationDelay: `${index * 0.2}s` }"></div>
              </div>
            </div>
          </div>
          
          <div class="section" v-if="financialDetails.workers.length > 0">
            <h3>SALÁRIOS DOS TRABALHADORES</h3>
            <div class="expense-list">
              <div class="expense-item" v-for="(worker, index) in financialDetails.workers" :key="worker.id">
                <div class="expense-info">
                  <span class="expense-name">{{ worker.name }}</span>
                  <span class="expense-value">
                    <span class="currency"><i class="coin-icon"></i></span>
                    <animated-number :value="worker.amount" />
                  </span>
                </div>
                <div class="transaction-line" :style="{ animationDelay: `${index * 0.2}s` }"></div>
              </div>
            </div>
          </div>
          
          <div class="section" v-if="financialDetails.specialWorkers.length > 0">
            <h3>SALÁRIOS DOS TRABALHADORES ESPECIAIS</h3>
            <div class="expense-list">
              <div class="expense-item" v-for="(worker, index) in financialDetails.specialWorkers" :key="worker.id">
                <div class="expense-info">
                  <span class="expense-name">{{ worker.name }}</span>
                  <span class="expense-value">
                    <span class="currency"><i class="coin-icon"></i></span>
                    <animated-number :value="worker.amount" />
                  </span>
                </div>
                <div class="transaction-line" :style="{ animationDelay: `${index * 0.2}s` }"></div>
              </div>
            </div>
          </div>
          
          <div class="total-section">
            <div class="glitch-container">
              <div class="total-label">TOTAL DE DESPESAS</div>
              <div class="total-value">
                <span class="currency"><i class="coin-icon"></i></span>
                <animated-number :value="financialDetails.total" :duration="2000" />
              </div>
            </div>
            
            <div class="balance-section" :class="{ 'negative': remainingCoins < 0 }">
              <div class="balance-label">SALDO RESTANTE</div>
              <div class="balance-value">
                <span class="currency"><i class="coin-icon"></i></span>
                <animated-number :value="remainingCoins" :duration="2500" />
              </div>
            </div>
          </div>
          
          <div class="warning-message" v-if="remainingCoins < 0">
            <div class="warning-icon">!</div>
            <div class="warning-text">
              ALERTA: SALDO NEGATIVO DETECTADO<br>
              PENALIDADES FINANCEIRAS PODEM SER APLICADAS
            </div>
          </div>
        </template>
        
        <template v-else>
          <div class="no-expenses">
            <div class="check-icon">✓</div>
            <div class="message">Sem despesas semanais neste momento</div>
          </div>
        </template>
      </div>
      
      <div class="modal-footer">
        <button @click="closeModal" class="close-btn">
          FECHAR <span class="btn-highlight">[ESC]</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import AnimatedNumber from '@/shared/components/common/AnimatedNumber.vue'

export default {
  name: 'WeekCostsModal',
  components: {
    AnimatedNumber
  },
  data() {
    return {
      visible: false,
      processingState: null, // null, 'processing', 'completed'
      financialDetails: {
        fighters: [],
        businesses: [],
        workers: [],
        specialWorkers: [],
        total: 0
      },
      remainingCoins: 0,
      initialBalance: 0,
      currentDate: ''
    };
  },
  computed: {
    player() {
      return this.$store.state.player.playerData || 'UNKNOWN';
    }
  },
  created() {
    // Listen for keydown events (ESC key)
    window.addEventListener('keydown', this.handleKeyDown);
    
    // Format the current date in cyberpunk style
    const now = new Date();
    this.currentDate = `${now.getFullYear()+60}.${String(now.getMonth()+1).padStart(2, '0')}.${String(now.getDate()).padStart(2, '0')}`;
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    handleKeyDown(e) {
      if (e.key === 'Escape' && this.visible) {
        this.closeModal();
      }
    },
    showModal(financialData) {
      if (!financialData) {
        // Sem processamento financeiro necessário
        this.visible = true;
        this.processingState = null;
        return;
      }
      
      this.visible = true;
      this.processingState = 'processing';
      
      // Simule o processamento para parecer mais um jogo
      setTimeout(async () => {
        this.processingState = 'completed';
        
        // Certifique-se de que estamos definindo os dados corretamente
        if (financialData.details) {
          this.financialDetails = {
            fighters: financialData.details.fighters || [],
            businesses: financialData.details.businesses || [],
            workers: financialData.details.workers || [],
            specialWorkers: financialData.details.specialWorkers || [],
            total: financialData.details.total || 0
          };
          
          // Calculando o saldo inicial (saldo atual + despesas)
          this.initialBalance = financialData.remainingCoins + financialData.details.total;
          this.remainingCoins = financialData.remainingCoins;
          await this.$store.dispatch('player/loadPlayerData');
        }
      }, 2000);
    },
    closeModal() {
      this.visible = false;
    }
  }
}
</script>

<style scoped lang="scss">
.week-costs-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
  perspective: 1000px;
  
  .modal-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.85);
    backdrop-filter: blur(4px);
    animation: fadeIn 0.3s ease-out;
  }
  
  .modal-content {
    position: relative;
    width: 90%;
    max-width: 800px;
    max-height: 90vh;
    background: rgba(20, 20, 25, 0.95);
    border: 1px solid #ff3e3e;
    box-shadow: 0 0 20px rgba(255, 62, 62, 0.5), 
                0 0 40px rgba(255, 62, 62, 0.2);
    border-radius: 4px;
    overflow: hidden;
    color: #f0f0f0;
    animation: modalOpen 0.5s cubic-bezier(0.165, 0.84, 0.44, 1);
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: linear-gradient(90deg, #ff3e3e, #ff9d6c, #ff3e3e);
      z-index: 1;
    }
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: rgba(255, 62, 62, 0.5);
    }
  }
  
  .modal-header {
    padding: 20px 25px;
    border-bottom: 1px solid rgba(255, 62, 62, 0.3);
    position: relative;
    background: rgba(30, 20, 20, 0.8);
    
    h2 {
      font-size: 1.8rem;
      margin: 0;
      letter-spacing: 1.5px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      
      .neon-text {
        color: #ff5c5c;
        text-shadow: 0 0 5px rgba(255, 92, 92, 0.7),
                    0 0 10px rgba(255, 92, 92, 0.5);
      }
      
      .date {
        font-size: 1.2rem;
        color: #ff9d6c;
        font-family: monospace;
      }
    }
    
    .corporate-id {
      font-size: 0.8rem;
      color: #888;
      letter-spacing: 1px;
      margin-top: 5px;
      font-family: monospace;
    }
  }
  
  .modal-body {
    padding: 25px;
    max-height: 60vh;
    overflow-y: auto;
    position: relative;
    
    &::-webkit-scrollbar {
      width: 8px;
    }
    
    &::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.2);
    }
    
    &::-webkit-scrollbar-thumb {
      background: #ff3e3e;
      border-radius: 4px;
    }
    
    .scanner-line {
      position: absolute;
      height: 2px;
      background: rgba(255, 62, 62, 0.7);
      width: 100%;
      left: 0;
      top: 0;
      box-shadow: 0 0 10px rgba(255, 62, 62, 0.7);
      animation: scanLine 4s infinite;
      z-index: 1;
      pointer-events: none;
    }
  }
  
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;
    
    .loading-text {
      font-size: 1.5rem;
      margin-bottom: 20px;
      color: #ff5c5c;
      font-family: monospace;
      letter-spacing: 2px;
      
      .dots {
        display: inline-block;
        animation: loadingDots 1.5s infinite;
        width: 30px;
        text-align: left;
      }
    }
    
    .progress-bar {
      width: 80%;
      height: 10px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 5px;
      overflow: hidden;
      position: relative;
      
      .progress-fill {
        position: absolute;
        height: 100%;
        background: linear-gradient(90deg, #ff3e3e, #ff9d6c);
        animation: progressFill 2s forwards;
        width: 0;
      }
    }
  }
  
  .current-balance {
    margin-bottom: 25px;
    padding: 15px;
    background: rgba(20, 40, 20, 0.6);
    border-radius: 4px;
    border-left: 3px solid #4ADE80;
    display: flex;
    flex-direction: column;
    
    .balance-title {
      font-size: 1.1rem;
      color: #aeffc3;
      margin-bottom: 8px;
      font-weight: bold;
      letter-spacing: 1px;
    }
    
    .balance-amount {
      font-size: 1.5rem;
      font-weight: bold;
      color: #4ADE80;
      font-family: monospace;
      
      .currency {
        margin-right: 6px;
        display: inline-flex;
        align-items: center;
        
        .coin-icon {
          display: inline-block;
          width: 20px;
          height: 20px;
          background: radial-gradient(circle at center, #ffce00 30%, #ff9d00 70%, #ff6400 100%);
          border-radius: 50%;
          box-shadow: 0 0 8px rgba(255, 200, 0, 0.6);
          position: relative;
          
          &::before {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 16px;
            height: 16px;
            transform: translate(-50%, -50%);
            background: linear-gradient(135deg, rgba(255, 217, 0, 0.9) 0%, rgba(255, 182, 0, 0.6) 100%);
            border-radius: 50%;
            z-index: 1;
          }
          
          &::after {
            content: 'C';
            font-size: 12px;
            font-weight: bold;
            font-family: monospace;
            color: #873500;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            z-index: 2;
          }
        }
      }
    }
  }
  
  .separator {
    display: flex;
    align-items: center;
    margin: 30px 0 25px;
    
    .separator-line {
      flex: 1;
      height: 1px;
      background: linear-gradient(90deg, transparent, #ff3e3e, transparent);
    }
    
    .separator-text {
      padding: 0 15px;
      font-size: 1rem;
      color: #ff9d6c;
      font-weight: bold;
      letter-spacing: 2px;
    }
  }
  
  .section {
    margin-bottom: 25px;
    position: relative;
    
    h3 {
      font-size: 1.2rem;
      color: #ff9d6c;
      margin-bottom: 15px;
      letter-spacing: 1.2px;
      text-transform: uppercase;
      position: relative;
      display: inline-block;
      
      &::after {
        content: '';
        position: absolute;
        bottom: -5px;
        left: 0;
        width: 100%;
        height: 1px;
        background: linear-gradient(90deg, #ff9d6c, transparent);
      }
    }
  }
  
  .expense-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  
  .expense-item {
    background: rgba(40, 30, 30, 0.6);
    border-left: 3px solid #ff3e3e;
    padding: 12px 15px;
    border-radius: 2px;
    position: relative;
    overflow: hidden;
    
    .expense-info {
      display: flex;
      justify-content: space-between;
      align-items: center;
      position: relative;
      z-index: 2;
    }
    
    .expense-name {
      font-size: 1rem;
      color: #f0f0f0;
    }
    
    .expense-value {
      font-size: 1.1rem;
      font-weight: bold;
      color: #ff3e3e;
      font-family: monospace;
      letter-spacing: 1px;
      
      .currency {
        margin-right: 4px;
        display: inline-flex;
        align-items: center;
        
        .coin-icon {
          display: inline-block;
          width: 16px;
          height: 16px;
          background: radial-gradient(circle at center, #ffce00 30%, #ff9d00 70%, #ff6400 100%);
          border-radius: 50%;
          position: relative;
          box-shadow: 0 0 5px rgba(255, 200, 0, 0.5);
          
          &::before {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 14px;
            height: 14px;
            transform: translate(-50%, -50%);
            background: linear-gradient(135deg, rgba(255, 217, 0, 0.9) 0%, rgba(255, 182, 0, 0.6) 100%);
            border-radius: 50%;
            z-index: 1;
          }
          
          &::after {
            content: 'C';
            font-size: 10px;
            font-weight: bold;
            font-family: monospace;
            color: #873500;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            z-index: 2;
          }
        }
      }
    }
    
    .transaction-line {
      position: absolute;
      height: 2px;
      background: rgba(255, 62, 62, 0.5);
      width: 0;
      bottom: 0;
      left: 0;
      animation: transactionLine 1s forwards;
    }
    
    &:hover {
      background: rgba(50, 35, 35, 0.8);
      transform: translateX(2px);
      transition: all 0.2s;
      
      .expense-name {
        color: #fff;
      }
    }
  }
  
  .total-section {
    margin-top: 30px;
    padding: 20px;
    background: rgba(40, 20, 20, 0.8);
    border: 1px solid rgba(255, 62, 62, 0.5);
    border-radius: 4px;
    position: relative;
    overflow: hidden;
    
    .glitch-container {
      margin-bottom: 15px;
      
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 2px;
        background: linear-gradient(90deg, transparent, #ff3e3e, transparent);
      }
    }
    
    .total-label {
      font-size: 1.3rem;
      color: #ff9d6c;
      letter-spacing: 1.5px;
      margin-bottom: 5px;
      font-weight: bold;
      text-transform: uppercase;
    }
    
    .total-value {
      font-size: 2.5rem;
      font-weight: bold;
      color: #ff3e3e;
      font-family: monospace;
      animation: glitch 5s infinite;
      text-shadow: 0 0 5px rgba(255, 62, 62, 0.7);
      
      .currency {
        margin-right: 8px;
        display: inline-flex;
        align-items: center;
        
        .coin-icon {
          display: inline-block;
          width: 24px;
          height: 24px;
          background: radial-gradient(circle at center, #ffce00 30%, #ff9d00 70%, #ff6400 100%);
          border-radius: 50%;
          position: relative;
          box-shadow: 0 0 10px rgba(255, 200, 0, 0.7);
          
          &::before {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 20px;
            height: 20px;
            transform: translate(-50%, -50%);
            background: linear-gradient(135deg, rgba(255, 217, 0, 0.9) 0%, rgba(255, 182, 0, 0.6) 100%);
            border-radius: 50%;
            z-index: 1;
          }
          
          &::after {
            content: 'C';
            font-size: 14px;
            font-weight: bold;
            font-family: monospace;
            color: #873500;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            z-index: 2;
          }
        }
      }
    }
    
    .balance-section {
      margin-top: 20px;
      padding-top: 15px;
      border-top: 1px dashed rgba(255, 255, 255, 0.2);
      
      &.negative .balance-value {
        color: #ff0000;
        animation: warningPulse 2s infinite;
      }
      
      .balance-label {
        font-size: 1.1rem;
        color: #ccc;
        letter-spacing: 1px;
        margin-bottom: 5px;
      }
      
      .balance-value {
        font-size: 1.8rem;
        font-weight: bold;
        color: #7aff6b;
        font-family: monospace;
        
        .currency {
          .coin-icon {
            animation: coinSpin 3s infinite linear;
          }
        }
      }
    }
  }
  
  .warning-message {
    margin-top: 20px;
    display: flex;
    align-items: center;
    background: rgba(80, 20, 20, 0.8);
    padding: 15px;
    border-radius: 4px;
    border-left: 5px solid #ff0000;
    animation: warningPulse 2s infinite;
    
    .warning-icon {
      font-size: 1.5rem;
      font-weight: bold;
      background: #ff0000;
      color: white;
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      margin-right: 15px;
    }
    
    .warning-text {
      color: #fff;
      line-height: 1.5;
    }
  }
  
  .no-expenses {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;
    
    .check-icon {
      font-size: 3rem;
      color: #4caf50;
      margin-bottom: 20px;
      background: rgba(76, 175, 80, 0.2);
      width: 80px;
      height: 80px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      animation: fadeInScale 0.5s;
    }
    
    .message {
      font-size: 1.3rem;
      color: #ccc;
    }
  }
  
  .modal-footer {
    padding: 15px 25px;
    display: flex;
    justify-content: flex-end;
    background: rgba(20, 15, 15, 0.9);
    
    .close-btn {
      background: #333;
      color: #fff;
      border: 1px solid #ff3e3e;
      padding: 8px 16px;
      font-size: 1rem;
      cursor: pointer;
      border-radius: 4px;
      transition: all 0.3s;
      letter-spacing: 1px;
      
      &:hover {
        background: #ff3e3e;
        box-shadow: 0 0 10px rgba(255, 62, 62, 0.7);
      }
      
      .btn-highlight {
        color: #ff9d6c;
        font-size: 0.8rem;
        margin-left: 5px;
      }
    }
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes modalOpen {
  from {
    transform: scale(0.9) rotateX(10deg);
    opacity: 0;
  }
  to {
    transform: scale(1) rotateX(0);
    opacity: 1;
  }
}

@keyframes scanLine {
  0% { top: 0; opacity: 0.7; }
  50% { top: 100%; opacity: 0.3; }
  100% { top: 0; opacity: 0.7; }
}

@keyframes loadingDots {
  0% { content: '.'; }
  33% { content: '..'; }
  66% { content: '...'; }
  100% { content: '.'; }
}

@keyframes progressFill {
  from { width: 0; }
  to { width: 100%; }
}

@keyframes transactionLine {
  from { width: 0; }
  to { width: 100%; }
}

@keyframes glitch {
  0%, 100% { text-shadow: 0 0 5px rgba(255, 62, 62, 0.7); }
  8%, 9% { text-shadow: -2px 0 red, 2px 2px blue; }
  82%, 83% { text-shadow: 2px -2px green, -2px 0 magenta; }
}

@keyframes warningPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

@keyframes fadeInScale {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes coinSpin {
  0% {
    transform: rotateY(0deg);
  }
  50% {
    transform: rotateY(180deg);
  }
  100% {
    transform: rotateY(360deg);
  }
}

@media (max-width: 768px) {
  .week-costs-modal {
    .modal-content {
      width: 95%;
      max-height: 95vh;
    }
    
    .modal-header {
      padding: 15px 20px;
      
      h2 {
        font-size: 1.4rem;
        flex-direction: column;
        align-items: flex-start;
        
        .date {
          font-size: 1rem;
          margin-top: 5px;
        }
      }
    }
    
    .modal-body {
      padding: 15px;
    }
    
    .expense-item {
      padding: 10px;
      
      .expense-name {
        font-size: 0.9rem;
      }
      
      .expense-value {
        font-size: 1rem;
      }
    }
    
    .total-section {
      padding: 15px;
      
      .total-label {
        font-size: 1.1rem;
      }
      
      .total-value {
        font-size: 1.8rem;
      }
    }
  }
}
</style>
